import { defineStore } from "pinia";
import db from "@/db";
import crudGenerator from "@/api/utils/offlineSync";
import { useProjectsStore } from "@/store/modules/projects";
import { useInstallationsStore } from "@/store/modules/installations";

const {
  innerFetch,
  innerCreate,
  innerPartialUpdate,
  innerBulkCreate,
  innerBulkUpdate,
  innerBulkDelete,
} = crudGenerator(db.models.Flow);

export const useFlowsStore = defineStore("flows", {
  state: () => ({
    flowList: [],
    flowIsLoading: false,
  }),
  actions: {
    async createFlow(form) {
      const flow = await innerCreate(form, useProjectsStore().isOfflineState);
      await useInstallationsStore().refreshLastUpdated();
      this.upsertFlow(flow);
      return flow;
    },

    async bulkCreateFlow(form) {
      const flows = await innerBulkCreate(
        form,
        useProjectsStore().isOfflineState
      );
      this.flowList = [...this.flowList, ...flows];
      await useInstallationsStore().refreshLastUpdated();
      return flows;
    },

    async updateFlow(form) {
      const flow = await innerPartialUpdate(
        form,
        useProjectsStore().isOfflineState
      );
      await useInstallationsStore().refreshLastUpdated();
      this.upsertFlow(flow);

      return flow;
    },

    async bulkUpdateFlows(form) {
      if (!form.length) return [];
      const flows = await innerBulkUpdate(
        form,
        useProjectsStore().isOfflineState
      );
      for (const flow of flows) {
        const index = this.flowList.findIndex(({ uuid }) => flow.uuid === uuid);
        this.flowList[index] = flow;
      }
      this.flowList = [...this.flowList];
      await useInstallationsStore().refreshLastUpdated();
      return flows;
    },

    async bulkDestroyFlows(flowsUuids) {
      const projectsStore = useProjectsStore();
      await innerBulkDelete(
        flowsUuids,
        projectsStore.isOfflineState,
        projectsStore.currentProjectUuid
      );
      await useInstallationsStore().refreshLastUpdated();
      this.flowList = this.flowList.filter(
        ({ uuid }) => !flowsUuids.includes(uuid)
      );
    },

    async fetchFlows(projectUuid) {
      return innerFetch(
        { project: projectUuid },
        useProjectsStore().isOfflineState,
        (isLoading) => {
          this.flowIsLoading = isLoading;
        }
      );
    },

    async filterFlow(vlesUuids) {
      while (this.flowIsLoading) await new Promise((r) => setTimeout(r, 200));
      this.flowIsLoading = true;

      try {
        const flows = await db.flows
          .filter(({ vle }) => vlesUuids.includes(vle))
          .toArray();

        for (const flow of flows) {
          await flow.setRelationships();
        }

        this.flowList = [...flows];
      } finally {
        this.flowIsLoading = false;
      }
    },

    upsertFlow(flow) {
      const index = this.flowList.findIndex(({ uuid }) => flow.uuid === uuid);

      if (index < 0) {
        this.flowList.push(flow);
      } else {
        this.flowList[index] = flow;
        this.flowList = [...this.flowList];
      }
    },
  },
});
